import { useContext, useMemo } from 'react';

import { UserContext } from 'constants/user-context';

/**
 * Le point de passage UNIQUE du masquage des prix.
 *
 * Certains clients ne doivent pas voir le prix de certains produits (consommables inclus dans un
 * forfait, par exemple). C'est purement un affichage : le prix réel s'applique toujours au panier, à
 * la commande et à la facture — on ne cache que le montant à l'écran.
 *
 * Aucun composant ne doit tester l'appartenance à la liste lui-même : passer par `<Price />`, qui
 * appelle ce hook. C'est ce qui évite de retomber sur l'ancien bricolage (une gamme de prix à 0, un
 * `prix > 0` ici et là, et un `0,00 €` qui réapparaît sur le troisième écran).
 *
 * Deux usages, et deux seulement :
 * - `isPriceVisible` / `<PriceIfVisible />` pour un montant rattaché à UN produit ;
 * - `keepVisiblePriceProducts` pour tout montant AGRÉGÉ (sous-total, total, minimums par flux).
 *   Un agrégat calculé sur la liste complète redonne le prix masqué dès que le panier ne contient
 *   qu'un produit masqué — c'est le trou que laissait le masquage limité au prix unitaire.
 */
type ProductWithId = { _id?: string | null };

export function usePriceVisibility(): {
    isPriceVisible: (productId: string | null | undefined) => boolean;
    keepVisiblePriceProducts: <T extends ProductWithId>(products: readonly T[]) => T[];
} {
    const { organization } = useContext(UserContext);
    const invisibleProductIds = organization?.invisiblePriceProductIds;

    const invisibleProductIdSet = useMemo(() => new Set(invisibleProductIds ?? []), [invisibleProductIds]);

    return useMemo(() => {
        // Sans produit identifié, on affiche : un total de panier n'appartient à aucun produit, et
        // masquer par défaut cacherait des montants à tout le monde.
        const isPriceVisible = (productId: string | null | undefined) =>
            !productId || !invisibleProductIdSet.has(productId);

        return {
            isPriceVisible,
            // Les produits masqués ne pèsent pas dans ce que le client VOIT — exactement ce que faisait
            // la gamme de prix à 0 €. Le prix réel continue d'alimenter la commande, Odoo et la facture.
            keepVisiblePriceProducts: <T extends ProductWithId>(products: readonly T[]) =>
                products.filter(({ _id }) => isPriceVisible(_id)),
        };
    }, [invisibleProductIdSet]);
}
